import { format } from 'date-fns';
import {
  AlignmentType,
  BorderStyle,
  convertInchesToTwip,
  ExternalHyperlink,
  FileChild,
  HeadingLevel,
  LevelFormat,
  Paragraph,
  ParagraphChild,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  TextRun,
  UnderlineType,
  WidthType,
} from 'docx';
import { DATETIME_FORMAT } from '@/app/const';

export const CONTENT_EMPTY = '—';

/** Must match `numbering.config` on the root `Document` in `exportToDocument/index.ts`. */
export const HTML_DOCX_NUMBERING_BULLET_REF = 'html-export-bullet';
export const HTML_DOCX_NUMBERING_ORDERED_REF = 'html-export-ordered';

const HTML_LIST_MAX_DEPTH = 6;

const TRANSPARENT_BLOCK_TAGS = new Set([
  'DIV',
  'SECTION',
  'ARTICLE',
  'MAIN',
  'CENTER',
  'FIGURE',
  'ASIDE',
  'HEADER',
  'FOOTER',
  'NAV',
  'FORM',
  'SPAN',
]);

type InlineMarks = {
  bold?: boolean;
  italics?: boolean;
  underline?: boolean;
  /** Inside `<a href>` — blue + underline so links read like Word hyperlinks */
  link?: boolean;
};

/** Word-style default hyperlink blue (RGB 5, 99, 193), without `#` per docx */
const HTML_EXPORT_LINK_COLOR = '0563C1';

export function formatSummaryDate(iso: string | null | undefined): string {
  if (!iso?.trim()) {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  return format(d, DATETIME_FORMAT);
}

export function sanitizeFileNameBase(name: string): string {
  const trimmed = name.trim() || 'module';
  return Array.from(trimmed, (c) => {
    const code = c.charCodeAt(0);
    if (code < 32) {
      return '_';
    }
    if (/[<>:"/\\|?*]/.test(c)) {
      return '_';
    }
    return c;
  }).join('');
}

export function keyValuePairParagraph(label: string, value: string): TextRun[] {
  return [
    new TextRun({ text: `${label}: `, bold: true }),
    new TextRun({ text: value }),
  ];
}

/** Removes non-content nodes before walking the DOM for export. */
export function stripNonExportableElements(doc: Document): void {
  doc.querySelectorAll('script, style, noscript').forEach((el) => el.remove());
}

export function getHtmlExportNumberingConfig(): {
  config: {
    reference: string;
    levels: {
      level: number;
      format: (typeof LevelFormat)[keyof typeof LevelFormat];
      text: string;
      alignment: (typeof AlignmentType)[keyof typeof AlignmentType];
      style: {
        paragraph: {
          indent: { left: number; hanging: number };
        };
      };
    }[];
  }[];
} {
  const levelIndent = (level: number) => ({
    left: convertInchesToTwip(0.22 * (level + 1)),
    hanging: convertInchesToTwip(0.18),
  });

  const bulletLevels = Array.from(
    { length: HTML_LIST_MAX_DEPTH },
    (_, level) => ({
      level,
      format: LevelFormat.BULLET,
      text: '\u2022',
      alignment: AlignmentType.START,
      style: {
        paragraph: {
          indent: levelIndent(level),
        },
      },
    }),
  );

  const orderedLevels = Array.from(
    { length: HTML_LIST_MAX_DEPTH },
    (_, level) => ({
      level,
      format: LevelFormat.DECIMAL,
      text: `%${level + 1}.`,
      alignment: AlignmentType.START,
      style: {
        paragraph: {
          indent: levelIndent(level),
        },
      },
    }),
  );

  return {
    config: [
      { reference: HTML_DOCX_NUMBERING_BULLET_REF, levels: bulletLevels },
      { reference: HTML_DOCX_NUMBERING_ORDERED_REF, levels: orderedLevels },
    ],
  };
}

function normalizeExternalHref(href: string): string | null {
  const trimmed = href.trim();
  if (!trimmed) {
    return null;
  }
  if (/^(https?|mailto):/i.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith('//')) {
    return `https:${trimmed}`;
  }
  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'https://local.invalid';
    return new URL(trimmed, base).href;
  } catch {
    return null;
  }
}

function textRunFromMarks(text: string, marks: InlineMarks): TextRun {
  const underline =
    marks.underline || marks.link ? { type: UnderlineType.SINGLE } : undefined;
  return new TextRun({
    text,
    bold: marks.bold,
    italics: marks.italics,
    underline,
    color: marks.link ? HTML_EXPORT_LINK_COLOR : undefined,
  });
}

const IMAGE_LINK_LABEL_MAX = 72;

/**
 * Short label for export (usually filename from path), not the full URL.
 * Hyperlink target still uses the full resolved URL elsewhere.
 */
function imageSrcDisplayName(resolved: string | null, srcRaw: string): string {
  const tryParse = (value: string): string | null => {
    const v = value.trim();
    if (!v) {
      return null;
    }
    if (/^data:/i.test(v)) {
      const mime = /^data:([^;,]+)/i.exec(v)?.[1]?.trim();
      if (mime) {
        const short = mime.split('/').pop() ?? mime;
        return `Embedded image (${short})`;
      }
      return 'Embedded image';
    }
    try {
      const base =
        typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : 'https://local.invalid';
      const u = new URL(v, base);
      const parts = u.pathname.split('/').filter(Boolean);
      const last = parts[parts.length - 1];
      if (last) {
        const baseName = last.split('?')[0].split('#')[0];
        try {
          return decodeURIComponent(baseName);
        } catch {
          return baseName;
        }
      }
    } catch {
      /* fall through */
    }
    const tail = /[/\\]([^/\\?#]+)(?:[?#]|$)/.exec(v)?.[1];
    if (tail) {
      try {
        return decodeURIComponent(tail);
      } catch {
        return tail;
      }
    }
    return null;
  };

  const fromResolved = resolved ? tryParse(resolved) : null;
  if (fromResolved) {
    return fromResolved.length > IMAGE_LINK_LABEL_MAX
      ? `${fromResolved.slice(0, IMAGE_LINK_LABEL_MAX)}…`
      : fromResolved;
  }
  const fromRaw = tryParse(srcRaw);
  if (fromRaw) {
    return fromRaw.length > IMAGE_LINK_LABEL_MAX
      ? `${fromRaw.slice(0, IMAGE_LINK_LABEL_MAX)}…`
      : fromRaw;
  }
  return 'Image';
}

/** Placeholder for images not embedded: `[ file name - alt ]`, hyperlink to asset when http(s) URL is available. */
function appendImageExportPlaceholder(
  img: HTMLImageElement,
  marks: InlineMarks,
  out: ParagraphChild[],
): void {
  const alt = img.getAttribute('alt')?.trim() ?? '';
  const srcRaw = (img.getAttribute('src') ?? '').trim();
  const resolved = srcRaw ? normalizeExternalHref(srcRaw) : null;
  const linkLabel = imageSrcDisplayName(resolved, srcRaw);
  const filePart = srcRaw ? linkLabel : 'no src';
  const altPart = alt.length > 0 ? alt : 'none';
  const bracketText = `[ ${filePart} - ${altPart} ]`;

  if (resolved && /^https?:\/\//i.test(resolved)) {
    out.push(
      new ExternalHyperlink({
        link: resolved,
        children: [
          new TextRun({
            text: bracketText,
            bold: marks.bold,
            italics: marks.italics,
            underline: { type: UnderlineType.SINGLE },
            color: HTML_EXPORT_LINK_COLOR,
          }),
        ],
      }),
    );
  } else {
    out.push(textRunFromMarks(bracketText, marks));
  }
}

function walkInline(
  node: Node,
  marks: InlineMarks,
  out: ParagraphChild[],
): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? '';
    if (text.length > 0) {
      out.push(textRunFromMarks(text, marks));
    }
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  const el = node as Element;
  const tag = el.tagName.toUpperCase();

  switch (tag) {
    case 'STRONG':
    case 'B':
      Array.from(el.childNodes).forEach((c) =>
        walkInline(c, { ...marks, bold: true }, out),
      );
      return;
    case 'EM':
    case 'I':
      Array.from(el.childNodes).forEach((c) =>
        walkInline(c, { ...marks, italics: true }, out),
      );
      return;
    case 'U':
      Array.from(el.childNodes).forEach((c) =>
        walkInline(c, { ...marks, underline: true }, out),
      );
      return;
    case 'BR':
      out.push(new TextRun({ text: '', break: 1 }));
      return;
    case 'IMG':
      appendImageExportPlaceholder(el as HTMLImageElement, marks, out);
      return;
    case 'A': {
      const href = el.getAttribute('href');
      const link = href ? normalizeExternalHref(href) : null;
      const inner: ParagraphChild[] = [];
      const linkMarks: InlineMarks = { ...marks, link: true };
      Array.from(el.childNodes).forEach((c) => walkInline(c, linkMarks, inner));
      if (inner.length === 0) {
        return;
      }
      if (link) {
        out.push(
          new ExternalHyperlink({
            children: inner,
            link,
          }),
        );
      } else {
        out.push(...inner);
      }
      return;
    }
    case 'P':
    case 'DIV':
    case 'SPAN':
    case 'FONT':
    case 'MARK':
    case 'SMALL':
    case 'SUB':
    case 'SUP':
    case 'CODE':
    case 'S':
    case 'STRIKE':
    case 'DEL':
      Array.from(el.childNodes).forEach((c) => walkInline(c, marks, out));
      return;
    default:
      Array.from(el.childNodes).forEach((c) => walkInline(c, marks, out));
  }
}

/** Converts inline / phrasing HTML under `root` to docx paragraph children (runs, links, breaks). */
export function htmlToInlineDocument(root: HTMLElement): ParagraphChild[] {
  const out: ParagraphChild[] = [];
  Array.from(root.childNodes).forEach((c) => walkInline(c, {}, out));
  return out.length > 0 ? out : [new TextRun('')];
}

const HEADING_MAP: Record<
  string,
  (typeof HeadingLevel)[keyof typeof HeadingLevel]
> = {
  H1: HeadingLevel.HEADING_1,
  H2: HeadingLevel.HEADING_2,
  H3: HeadingLevel.HEADING_3,
  H4: HeadingLevel.HEADING_4,
  H5: HeadingLevel.HEADING_5,
  H6: HeadingLevel.HEADING_6,
};

/* eslint-disable no-use-before-define -- table vs nodeToBlocks are mutually recursive */

export function horizontalRuleParagraph(): Paragraph {
  return new Paragraph({
    border: {
      bottom: {
        color: '999999',
        space: 1,
        style: BorderStyle.SINGLE,
        size: 6,
      },
    },
  });
}

const HTML_EXPORT_TABLE_BODY_WIDTH_TWIPS = convertInchesToTwip(6.5);

/** Equal-width grid columns summing to ~{@link HTML_EXPORT_TABLE_BODY_WIDTH_TWIPS}; not HTML-driven. */
function htmlExportTableColumnWidthsTwips(columnCount: number): number[] {
  const n = Math.max(columnCount, 1);
  const per = Math.floor(HTML_EXPORT_TABLE_BODY_WIDTH_TWIPS / n);
  return Array.from({ length: n }, () => per);
}

function getDirectTableRows(table: HTMLTableElement): HTMLTableRowElement[] {
  return Array.from(table.children).flatMap((section) => {
    const sectionTag = section.tagName.toUpperCase();
    if (
      sectionTag === 'THEAD' ||
      sectionTag === 'TBODY' ||
      sectionTag === 'TFOOT'
    ) {
      return Array.from(section.children).filter(
        (tr) => tr.tagName.toUpperCase() === 'TR',
      ) as HTMLTableRowElement[];
    }
    if (sectionTag === 'TR') {
      return [section as HTMLTableRowElement];
    }
    return [];
  });
}

function tableToFileChild(table: HTMLTableElement): FileChild | null {
  const trs = getDirectTableRows(table);
  const rows: TableRow[] = trs
    .map((tr) => {
      const cells: TableCell[] = Array.from(
        tr.querySelectorAll(':scope > th, :scope > td'),
      ).map(
        (cell) =>
          new TableCell({
            children: Array.from(cell.childNodes).flatMap((n) =>
              nodeToBlocks(n),
            ),
          }),
      );
      return cells.length > 0 ? new TableRow({ children: cells }) : null;
    })
    .filter((row): row is TableRow => row !== null);

  if (rows.length === 0) {
    return null;
  }

  const columnCount = Math.max(...rows.map((r) => r.CellCount), 1);

  return new Table({
    rows,
    width: { size: 100, type: WidthType.PERCENTAGE },
    layout: TableLayoutType.AUTOFIT,
    columnWidths: htmlExportTableColumnWidthsTwips(columnCount),
  });
}

function liToBlocks(
  li: HTMLLIElement,
  ordered: boolean,
  depth: number,
): FileChild[] {
  const numbering = {
    reference: ordered
      ? HTML_DOCX_NUMBERING_ORDERED_REF
      : HTML_DOCX_NUMBERING_BULLET_REF,
    level: Math.min(depth, HTML_LIST_MAX_DEPTH - 1),
  };

  const out: FileChild[] = [];
  const wrap = document.createElement('div');
  const nestedLists: Element[] = [];

  Array.from(li.childNodes).forEach((n) => {
    if (
      n.nodeType === Node.ELEMENT_NODE &&
      ['UL', 'OL'].includes((n as Element).tagName.toUpperCase())
    ) {
      nestedLists.push(n as Element);
    } else {
      wrap.appendChild(n.cloneNode(true));
    }
  });

  const hasLeadNodes = wrap.childNodes.length > 0;
  if (hasLeadNodes || nestedLists.length === 0) {
    out.push(
      new Paragraph({
        children: htmlToInlineDocument(wrap),
        numbering,
      }),
    );
  }

  nestedLists.forEach((list) => {
    if (list.tagName.toUpperCase() === 'UL') {
      out.push(...ulToBlocks(list as HTMLUListElement, depth + 1));
    } else {
      out.push(...olToBlocks(list as HTMLOListElement, depth + 1));
    }
  });

  if (out.length === 0) {
    out.push(new Paragraph({ children: [new TextRun('')], numbering }));
  }

  return out;
}

function ulToBlocks(ul: HTMLUListElement, depth: number): FileChild[] {
  return Array.from(ul.children)
    .filter((li) => li.tagName.toUpperCase() === 'LI')
    .flatMap((li) => liToBlocks(li as HTMLLIElement, false, depth));
}

function olToBlocks(ol: HTMLOListElement, depth: number): FileChild[] {
  return Array.from(ol.children)
    .filter((li) => li.tagName.toUpperCase() === 'LI')
    .flatMap((li) => liToBlocks(li as HTMLLIElement, true, depth));
}

function nodeToBlocks(node: Node): FileChild[] {
  if (node.nodeType === Node.TEXT_NODE) {
    const t = (node.textContent ?? '').replace(/\s+/g, ' ').trim();
    return t ? [new Paragraph({ children: [new TextRun(t)] })] : [];
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return [];
  }

  const el = node as HTMLElement;
  const tag = el.tagName.toUpperCase();

  if (TRANSPARENT_BLOCK_TAGS.has(tag)) {
    return flatMapChildBlocks(el);
  }

  switch (tag) {
    case 'P':
      return [
        new Paragraph({
          children: htmlToInlineDocument(el),
        }),
      ];
    case 'H1':
    case 'H2':
    case 'H3':
    case 'H4':
    case 'H5':
    case 'H6':
      return [
        new Paragraph({
          heading: HEADING_MAP[tag],
          children: htmlToInlineDocument(el),
        }),
      ];
    case 'HR':
      return [horizontalRuleParagraph()];
    case 'UL':
      return ulToBlocks(el as HTMLUListElement, 0);
    case 'OL':
      return olToBlocks(el as HTMLOListElement, 0);
    case 'TABLE': {
      const t = tableToFileChild(el as HTMLTableElement);
      return t ? [t] : [];
    }
    case 'BR':
      return [
        new Paragraph({ children: [new TextRun({ text: '', break: 1 })] }),
      ];
    case 'LI':
      return liToBlocks(el as HTMLLIElement, false, 0);
    case 'IMG': {
      const img = el as HTMLImageElement;
      const children: ParagraphChild[] = [];
      appendImageExportPlaceholder(img, {}, children);
      return [new Paragraph({ children })];
    }
    default:
      return flatMapChildBlocks(el);
  }
}

function flatMapChildBlocks(el: HTMLElement): FileChild[] {
  return Array.from(el.childNodes).flatMap((c) => nodeToBlocks(c));
}

/** Walks block-level structure under `body` and returns sibling `FileChild` nodes (paragraphs, tables). */
export function htmlToBlocksDocument(body: HTMLElement): FileChild[] {
  return flatMapChildBlocks(body);
}

/* eslint-enable no-use-before-define */

/**
 * Parses HTML and returns block-level docx nodes (paragraphs, tables) preserving coarse structure.
 * Images are not embedded; each becomes `[ file name - alt ]` with a clickable http(s) link to the asset when possible.
 */
export function htmlContentHandler(html: string): FileChild[] {
  if (!html?.trim()) {
    return [];
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  stripNonExportableElements(doc);
  return htmlToBlocksDocument(doc.body);
}

/** Parses HTML and returns a single paragraph whose text is the concatenated content of all `<script>` tags. */
export function scriptTagContentHandler(html: string): FileChild[] {
  if (!html?.trim()) {
    return [];
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const scriptContent = Array.from(doc.querySelectorAll('script'))
    .map((el) => el.textContent ?? '')
    .join('')
    .trim();
  if (!scriptContent) {
    return [];
  }
  return [new Paragraph({ children: [new TextRun(scriptContent)] })];
}

/**
 * Parses HTML and returns a table of all elements that carry an inline `onclick` attribute.
 * Columns: element tag | identifiers (id + classes) | onclick value.
 */
export function onclickContentHandler(html: string): FileChild[] {
  if (!html?.trim()) {
    return [];
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const elements = Array.from(doc.querySelectorAll('[onclick]'));
  if (elements.length === 0) {
    return [];
  }

  const makeCell = (text: string, bold?: boolean) =>
    new TableCell({
      children: [
        new Paragraph({ text: '', children: [new TextRun({ text, bold })] }),
      ],
    });

  const headerRow = new TableRow({
    children: [
      makeCell('Element', true),
      makeCell('Identifiers', true),
      makeCell('onclick', true),
    ],
  });

  const dataRows = elements.map((el) => {
    const tag = el.tagName.toLowerCase();

    const id = el.getAttribute('id')?.trim() ?? '';
    const cls = el.getAttribute('class')?.trim() ?? '';
    const identifierParts: string[] = [];
    if (id) {
      identifierParts.push(`#${id}`);
    }
    if (cls) {
      identifierParts.push(
        cls
          .split(/\s+/)
          .map((c) => `.${c}`)
          .join(' '),
      );
    }
    const identifiers = identifierParts.join(' ') || CONTENT_EMPTY;

    const onclick = el.getAttribute('onclick')?.trim() || CONTENT_EMPTY;

    return new TableRow({
      children: [makeCell(tag), makeCell(identifiers), makeCell(onclick)],
    });
  });

  return [
    new Table({
      rows: [headerRow, ...dataRows],
      width: { size: 100, type: WidthType.PERCENTAGE },
      layout: TableLayoutType.AUTOFIT,
      columnWidths: htmlExportTableColumnWidthsTwips(3),
    }),
  ];
}
